/* ==========================================================================
   RACINEA — ofertas del carrito (cajón lateral y página /cart)

   Dos botones, dos gestos:
     · [data-mtc-anadir]  → añade el sérum al carrito.
     · [data-mtc-cambiar] → cambia el pack por el de más unidades: primero
       añade el nuevo y luego quita el viejo, en ese orden, para que el carrito
       no se quede vacío por el camino ni el cajón se cierre solo.

   Después de tocar el carrito hay que avisar al tema, o el cajón se queda
   pintando lo de antes. El tema escucha el evento cartLinesUpdate de Shopify y
   repinta su sección con el HTML que le llegue en la promesa, así que aquí se
   dispara ese mismo evento y se resuelve con las secciones recién generadas.

   Un aviso aprendido a base de golpes en este proyecto: los guiones del tema se
   cargan desde varios sitios y todo lo global acaba corriendo varias veces. Por
   eso aquí NO se enganchan listeners a cada botón, sino uno solo al documento
   (delegación), y se marca window para no montarlo dos veces.
   ========================================================================== */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

if (!window.mtCarritoListo) {
  window.mtCarritoListo = true;

  /* El tema declara 'const Theme = {...}', que NO cuelga de window: hay que
     leerlo a pelo con typeof. Con window.Theme esto caia a las rutas sin
     prefijo de idioma. */
  const RUTAS_TEMA = typeof Theme !== 'undefined' ? Theme.routes : null;
  const RUTAS = {
    add: RUTAS_TEMA?.cart_add_url || '/cart/add.js',
    update: RUTAS_TEMA?.cart_update_url || '/cart/update',
  };

  const CABECERAS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  /* Los nombres de sección hay que sacarlos del propio HTML, no inventarlos.
     En el cajón la sección se llama "cart-drawer-section" a secas, pero las de
     una plantilla llevan delante un trozo que cambia en cada tienda
     ("template--31749852922181__mt-carrito"). Si se pide el nombre corto,
     Shopify no devuelve nada y el bloque se queda con lo de antes. */
  function idSeccion(sufijo) {
    const el = document.querySelector(`[id^="shopify-section-"][id$="${sufijo}"]`);
    return el ? el.id.replace(/^shopify-section-/, '') : null;
  }

  function seccionesPresentes() {
    return ['cart-drawer-section', 'cart-section', 'mt-carrito']
      .map(idSeccion)
      .filter(Boolean);
  }

  /* Una llamada sin cambios que devuelve el carrito entero y las secciones ya
     renderizadas. Se usa como último paso de cada gesto para tener siempre la
     misma forma de respuesta, venga de un añadido o de un cambio de pack. */
  async function carritoYSecciones() {
    const respuesta = await fetch(RUTAS.update, {
      method: 'POST',
      headers: CABECERAS,
      body: JSON.stringify({
        updates: {},
        sections: seccionesPresentes().join(','),
        sections_url: window.location.pathname,
      }),
    });
    if (!respuesta.ok) throw new Error(`update ${respuesta.status}`);
    return respuesta.json();
  }

  /* Repinta la sección propia de la página /cart. El tema no sabe que existe,
     así que de esta me encargo yo. En el cajón no hace falta: mis bloques van
     dentro de cart-drawer-section y el tema ya la repinta entera. */
  function repintarSeccionPropia(secciones) {
    const id = idSeccion('mt-carrito');
    if (!id) return;
    const html = secciones?.[id];
    const destino = document.getElementById(`shopify-section-${id}`);
    if (!html || !destino) return;
    const nuevo = new DOMParser().parseFromString(html, 'text/html');
    const dentro = nuevo.getElementById(`shopify-section-${id}`);
    destino.innerHTML = dentro ? dentro.innerHTML : nuevo.body.innerHTML;
  }

  function estadoBoton(boton, ocupado) {
    if (!boton) return;
    boton.disabled = ocupado;
    boton.setAttribute('aria-busy', ocupado ? 'true' : 'false');
  }

  /**
   * Ejecuta el cambio en el carrito y avisa al tema.
   * @param {() => Promise<any>} mutacion  lo que toca el carrito
   * @param {'add'|'update'} accion        para el evento del tema
   * @param {string} varianteId
   */
  async function tocarCarrito(mutacion, accion, varianteId) {
    const diferida = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: accion,
        context: 'cart',
        lines: [{ id: String(varianteId), quantity: 1 }],
        promise: diferida.promise,
      })
    );

    try {
      await mutacion();
      const carrito = await carritoYSecciones();

      diferida.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
        detail: {
          sections: carrito.sections,
          items: carrito.items,
          itemCount: carrito.item_count,
          source: 'mt-carrito',
          didError: false,
        },
      });

      repintarSeccionPropia(carrito.sections);
      return true;
    } catch (error) {
      diferida.reject(error);
      console.warn('[mt-carrito] no se pudo cambiar el carrito:', error);
      return false;
    }
  }

  /* ---------- Añadir el sérum ---------- */

  async function anadir(boton) {
    const id = Number(boton.dataset.variante);
    if (!id) return;

    estadoBoton(boton, true);
    const bien = await tocarCarrito(
      async () => {
        const r = await fetch(RUTAS.add, {
          method: 'POST',
          headers: CABECERAS,
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
        if (!r.ok) throw new Error(`add ${r.status}`);
        return r.json();
      },
      'add',
      id
    );

    /* Si salió bien, el bloque desaparece solo al repintarse (el sérum ya está
       en el carrito). Si falló, se devuelve el botón a su sitio para que se
       pueda reintentar en vez de dejarlo muerto. */
    if (!bien) estadoBoton(boton, false);
  }

  /* ---------- Subir de pack ---------- */

  async function cambiar(boton) {
    const nueva = Number(boton.dataset.variante);
    const clave = boton.dataset.clave;
    if (!nueva || !clave) return;

    /* Pack con regalo: el sérum entra en la misma llamada que el pack. En el
       pedido es una línea real; lo deja a 0 el descuento automático de Shopify. */
    const regalo = Number(boton.dataset.regalo);
    const lineas = [{ id: nueva, quantity: 1 }];
    if (regalo) lineas.push({ id: regalo, quantity: 1 });
    const regalo2 = Number(boton.dataset.regalo2);
    if (regalo2) lineas.push({ id: regalo2, quantity: 1 });
    const regalo3 = Number(boton.dataset.regalo3);
    if (regalo3) lineas.push({ id: regalo3, quantity: 1 });

    estadoBoton(boton, true);
    const bien = await tocarCarrito(
      async () => {
        /* Primero entra el pack nuevo... */
        const r1 = await fetch(RUTAS.add, {
          method: 'POST',
          headers: CABECERAS,
          body: JSON.stringify({ items: lineas }),
        });
        if (!r1.ok) throw new Error(`add ${r1.status}`);

        /* ...y solo entonces sale el viejo. Por la clave de línea y no por su
           número de orden: al añadir, ese número se mueve. */
        const r2 = await fetch(RUTAS.update, {
          method: 'POST',
          headers: CABECERAS,
          body: JSON.stringify({ updates: { [clave]: 0 } }),
        });
        if (!r2.ok) throw new Error(`update ${r2.status}`);
        return r2.json();
      },
      'update',
      nueva
    );

    if (!bien) estadoBoton(boton, false);
  }

  /* ---------- Un solo listener para todo ---------- */

  document.addEventListener('click', (evento) => {
    const objetivo = evento.target instanceof Element ? evento.target : null;
    if (!objetivo) return;

    const btnAnadir = objetivo.closest('[data-mtc-anadir]');
    if (btnAnadir) {
      evento.preventDefault();
      anadir(btnAnadir);
      return;
    }

    const btnCambiar = objetivo.closest('[data-mtc-cambiar]');
    if (btnCambiar) {
      evento.preventDefault();
      cambiar(btnCambiar);
    }
  });

  /* Si el carrito cambia por otro sitio (la cantidad de una línea, el botón de
     borrar), la sección propia de la página /cart se queda desfasada y podría
     ofrecer un pack que ya no toca. Aquí se repinta. El cajón no lo necesita. */
  let pendiente = null;
  document.addEventListener(StandardEvents.cartLinesUpdate, (evento) => {
    if (evento.detail?.source === 'mt-carrito') return;
    const id = idSeccion('mt-carrito');
    if (!id) return;

    clearTimeout(pendiente);
    pendiente = setTimeout(async () => {
      try {
        const r = await fetch(
          `${window.location.pathname}?sections=${encodeURIComponent(id)}`,
          { headers: { Accept: 'application/json' } }
        );
        if (!r.ok) return;
        repintarSeccionPropia(await r.json());
      } catch (error) {
        console.warn('[mt-carrito] no se pudo refrescar la sección:', error);
      }
    }, 250);
  });
}
